/**
 * The live section session: what a section leader records in the section
 * slides while teaching one section week. Who is here, whose turn it is, and
 * which student drove which twist. The slides write it as the section runs,
 * and the SL debrief reads it afterwards. Its present/absent marks are the
 * section leader's own record: official attendance comes only from a
 * student's Join click (`recordSectionAttendance`), which also marks the
 * student present here.
 *
 * One document per section week, at
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/liveSession/current`.
 * It lives under `sectionAnalysis` rather than under the section document
 * because every section subcollection is world-readable, and this document
 * pairs student uids with attendance and timestamps.
 *
 * Practice presentations keep the same state shape in the presenter's
 * browser and never write this document.
 */

import {z} from 'zod';

import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import type {CourseId} from '../../../types/branded';
import {FirestoreTimestampSchema} from '../../types';
import {
  getSectionAnalysisWeekChildCollectionPath,
  SECTION_ANALYSIS_LIVE_SESSION_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';

/** The single document id in the `liveSession` collection. */
const LIVE_SESSION_DOCUMENT_ID = 'current' as const;

/** One person on the section's roster. */
const SessionStudentSchema = z.object({
  /**
   * Stable id within the session. A student with an account uses their uid;
   * a name the section leader typed gets a random id.
   */
  id: z.string(),
  /**
   * The student's Code in Place uid. Absent for a name the section leader
   * typed, which the debrief lists but cannot rate.
   */
  uid: z.string().optional(),
  /** The name shown in the slides: the user's display name, or as typed. */
  name: z.string(),
  /** Whether the section leader marked the student here. */
  isPresent: z.boolean().default(true),
  /** Whether the student has had a turn this round. */
  hasHadTurn: z.boolean().default(false),
  /** Kept when other students are removed, so a student's color is stable. */
  colorIndex: z.number().default(0),
});

/** One person on the section's roster. */
export type SessionStudent = z.infer<typeof SessionStudentSchema>;

/** The navigator of a turn driven in a pair. */
const SessionNavigatorSchema = z.object({
  /** The navigator's {@link SessionStudent.id}. */
  studentId: z.string(),
  /** The navigator's uid; absent for a typed name. */
  uid: z.string().optional(),
  /** The navigator's name when picked. */
  name: z.string(),
});

/**
 * One twist turn: a student was picked to drive, maybe with a second student
 * navigating, and maybe given a twist.
 */
const SessionTurnSchema = z.object({
  /** Stable id of the turn. */
  id: z.string(),
  /** The driver's {@link SessionStudent.id}. */
  studentId: z.string(),
  /** The driver's uid; absent for a typed name. */
  uid: z.string().optional(),
  /** The driver's name when picked. */
  name: z.string(),
  /**
   * The second student, when the turn was driven in a pair. A malformed
   * value reads as none, so it can never fail the whole session.
   */
  navigator: SessionNavigatorSchema.optional().catch(undefined),
  /** The twist the driver got, once a twist slide chose one. */
  twistId: z.string().optional(),
  /** The twist's title when it was chosen, for display. */
  twistTitle: z.string().optional(),
  /** Epoch ms of the pick, on the presenter's clock. */
  pickedAt: z.number(),
  /** The {@link SectionSessionState.round} the turn was taken in. */
  round: z.number().int().nonnegative().catch(0),
});

/** One twist turn. */
export type SessionTurn = z.infer<typeof SessionTurnSchema>;

/** A twist the section leader planned for a student before section. */
const TwistAssignmentSchema = z.object({
  /** The student's Code in Place uid. */
  uid: z.string(),
  /** The twist's id in the twist library. */
  twistId: z.string(),
  /**
   * Planned turn, from 1. The slides give the next turn to the waiting
   * student with the lowest planned turn; students without one go in random
   * order after them. Absent on plans made by hand.
   */
  turn: z.number().int().positive().optional(),
});

/** A twist the section leader planned for a student before section. */
export type TwistAssignment = z.infer<typeof TwistAssignmentSchema>;

/** The state the section tools share for one session. */
export const SectionSessionStateSchema = z.object({
  /** Everyone on the roster, here or not, in the order they were added. */
  students: z.array(SessionStudentSchema).default([]),
  /** The student picked last. */
  lastPickedId: z.string().nullable().default(null),
  /** Epoch ms of the last pick, which starts that student's turn. */
  lastPickedAt: z.number().nullable().default(null),
  /** Every turn, in the order they happened. */
  turns: z.array(SessionTurnSchema).default([]),
  /**
   * Counts the rounds of turns. A round ends when everyone here has had a
   * turn, or when the section leader starts the round over.
   */
  round: z.number().int().nonnegative().catch(0),
  /**
   * Twists planned for students before section, in the order planned. A
   * student's first assignment among a twist slide's twists is theirs on
   * that slide. A malformed list reads as empty, so it can never fail the
   * whole session.
   */
  twistAssignments: z.array(TwistAssignmentSchema).catch([]),
});

/** The state the section tools share for one session. */
export type SectionSessionState = z.infer<typeof SectionSessionStateSchema>;

/** A session nobody has added to yet. */
export const EMPTY_SECTION_SESSION_STATE: SectionSessionState = {
  students: [],
  lastPickedId: null,
  lastPickedAt: null,
  turns: [],
  round: 0,
  twistAssignments: [],
};

/** The live session document for one section week. */
export const LiveSessionDocumentSchema = SectionSessionStateSchema.extend({
  /** Denormalized from the path; rules check it. */
  courseId: z.string(),
  /** Denormalized from the path; rules check it. */
  sectionId: z.string(),
  /** Denormalized from the path, 1-based like `slDebriefs`. */
  sectionWeek: z.number(),
  /** The deck presented last. */
  deckId: z.string().optional(),
  /** Who wrote last. */
  updatedBy: z.string(),
  /**
   * Counts the saved changes, so a reader can tell a newer state from an
   * older one that arrives late.
   */
  revision: z.number().int().nonnegative().default(0),
  /** Server time of the last write. */
  updatedAt: FirestoreTimestampSchema.optional(),
}).loose();

/**
 * Document path of a section week's live session.
 *
 * @param params.courseId - The course.
 * @param params.sectionId - The section.
 * @param params.sectionWeek - The week, 1-based.
 * @return The document path.
 * @throws Never.
 */
export function getLiveSessionDocumentPath(params: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  const collection = getSectionAnalysisWeekChildCollectionPath({
    ...params,
    leafSubcollection: SECTION_ANALYSIS_LIVE_SESSION_SUBCOLLECTION,
  });
  return `${collection}/${LIVE_SESSION_DOCUMENT_ID}`;
}
